'use client'

import { isArray, reject } from 'lodash'
import { usePathname } from 'next/navigation'
import { useCallback } from 'react'

import { useCurrentUser } from '~/data/api/hooks/users'
import { logger } from '~/debug/logger'

import type { RecentActivityItem, SearchCategory } from '../types'
import { CATEGORY_ORDER, MAX_RECENT_ITEMS } from '../types'

const STORAGE_KEY = 'recentActivity'

const VALID_CATEGORIES = new Set<string>(CATEGORY_ORDER)

export type TrackVisitInput = {
  id: string
  path?: string
  category: SearchCategory
  label: string
  subLabel?: string
}

type UseRecentActivityReturn = {
  trackVisit: (item: TrackVisitInput) => void
  getRecentItems: () => RecentActivityItem[]
}

function isValidItem(item: unknown): item is RecentActivityItem {
  if (typeof item !== 'object' || item === null) return false
  const obj = item as Record<string, unknown>
  return (
    typeof obj.id === 'string' &&
    typeof obj.path === 'string' &&
    typeof obj.category === 'string' &&
    VALID_CATEGORIES.has(obj.category) &&
    typeof obj.label === 'string' &&
    typeof obj.timestamp === 'number'
  )
}

function readItems(userId: string): RecentActivityItem[] {
  if (typeof window === 'undefined') return []

  try {
    const stored = localStorage.getItem(`${userId}/${STORAGE_KEY}`)
    if (!stored) return []
    const parsed: unknown = JSON.parse(stored)
    if (!isArray(parsed)) return []
    return parsed.filter(isValidItem).slice(0, MAX_RECENT_ITEMS)
  } catch (error) {
    logger.warn('Failed to read recent activity from localStorage', error)
    return []
  }
}

function writeItems(userId: string, items: RecentActivityItem[]): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(`${userId}/${STORAGE_KEY}`, JSON.stringify(items))
  } catch (error) {
    logger.warn('Failed to write recent activity to localStorage', error)
  }
}

/**
 * Removes every per-user recent-activity entry from localStorage.
 *
 * Called when entering or exiting demo mode so Command-K / recent activity never
 * carries real customer names into a demo session (or anonymized labels back out).
 */
export function clearAllRecentActivity(): void {
  if (typeof window === 'undefined') return

  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.endsWith(`/${STORAGE_KEY}`)) {
        keysToRemove.push(key)
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key)
    }
  } catch (error) {
    logger.warn('Failed to clear recent activity from localStorage', error)
  }
}

/**
 * Manages recently viewed pages/entities in localStorage.
 *
 * - `trackVisit`: records a page visit, moving it to the front of the list
 * - `getRecentItems`: reads from localStorage, excluding the current path
 *
 * Both are no-ops when the feature gate is off.
 */
export default function useRecentActivity(): UseRecentActivityReturn {
  const { synopUser } = useCurrentUser()
  const userId = synopUser?.id ?? ''
  const pathname = usePathname()

  const trackVisit = useCallback(
    (input: TrackVisitInput) => {
      if (!userId) return

      const path = input.path ?? pathname
      const newItem: RecentActivityItem = { ...input, path, timestamp: Date.now() }
      const current = readItems(userId)
      const filtered = reject(current, { id: newItem.id, category: newItem.category })
      const updated = [newItem, ...filtered].slice(0, MAX_RECENT_ITEMS)
      writeItems(userId, updated)
    },
    [userId, pathname]
  )

  const getRecentItems = useCallback((): RecentActivityItem[] => {
    if (!userId) return []

    return readItems(userId).filter((item) => {
      if (item.path === pathname) return false
      if (item.category === 'page' && pathname.startsWith(item.path + '/')) return false
      return true
    })
  }, [userId, pathname])

  return { trackVisit, getRecentItems }
}
